import React, { useCallback, useState } from 'react';
import { CheckIcon, ChevronsUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Icons } from '../icons';

export type ComboboxOption = {
  value: string | number;
  label: string;
};

/**
 * @deprecated Use QueryCombobox for new query selectors. It supports single and
 * multiple selection, search, and custom option/value rendering.
 */
export interface IComboboxProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  placeholder?: string;
  value?: ComboboxOption | null;
  onChangeValue?: (value?: ComboboxOption) => void;
  options: ComboboxOption[];
  showSearch?: boolean;
  searchPlaceholder?: string;
  searchValue?: string;
  onChangeSearchValue?: (value: string) => void;
  emptyText?: string;
  isLoading?: boolean;
  className?: string;
  renderOption?: (option: ComboboxOption) => React.ReactNode;
}

/**
 * @deprecated Use QueryCombobox for new query selectors. It supports single and
 * multiple selection, search, and custom option/value rendering.
 */
const Combobox: React.FC<IComboboxProps> = ({
  open,
  onOpenChange,
  value,
  onChangeValue,
  options,
  placeholder = 'Select...',
  showSearch = false,
  searchPlaceholder = 'Search...',
  searchValue,
  onChangeSearchValue,
  emptyText = 'No options found.',
  isLoading,
  className,
  renderOption = (option) => <span className="truncate">{option.label}</span>,
}) => {
  const [internalOpen, setInternalOpen] = useState(open || false);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setInternalOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  return (
    <Popover open={open || internalOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          aria-expanded={open}
          role="combobox"
          variant="outline"
          className={cn(
            'w-full justify-between border-input bg-background px-3 font-normal',
            'outline-none outline-offset-0 hover:bg-background focus-visible:outline-[3px]',
            className,
          )}
        >
          {value ? (
            renderOption(value)
          ) : (
            <span className="truncate text-muted-foreground">
              {placeholder}
            </span>
          )}
          {isLoading ? (
            <Icons.spinner
              aria-hidden="true"
              className="mr-2 h-4 w-4 animate-spin"
            />
          ) : (
            <ChevronsUpDown
              aria-hidden="true"
              className="ml-2 h-4 w-4 shrink-0 opacity-50"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-full min-w-[var(--radix-popper-anchor-width)] border-input p-0"
      >
        <Command filter={() => 1}>
          {showSearch && (
            <CommandInput
              placeholder={searchPlaceholder}
              value={searchValue}
              onValueChange={onChangeSearchValue}
            />
          )}
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value.toString()}
                  onSelect={(currentValue) => {
                    const selected = options.find(
                      ({ value }) => value.toString() === currentValue,
                    );
                    const isSame = selected?.value === value?.value;

                    onChangeValue?.(isSame ? undefined : selected);
                    handleOpenChange(false);
                  }}
                >
                  {renderOption(option)}

                  {value?.value === option.value && (
                    <CheckIcon className="ml-auto" size={16} />
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default Combobox;
